import { useState, useCallback, useEffect } from 'react';
import { languageAPI } from '../api/client';

interface UseTranslationOptions {
  userLanguage: string;
  contactLanguage: string | null;
}

export function useTranslation({ userLanguage, contactLanguage }: UseTranslationOptions) {
  const hasLanguageMismatch = !!contactLanguage && contactLanguage !== userLanguage;
  const [translateEnabled, setTranslateEnabled] = useState(hasLanguageMismatch);

  // Sync translateEnabled when the language mismatch state changes (e.g. switching contacts)
  useEffect(() => {
    setTranslateEnabled(hasLanguageMismatch);
  }, [hasLanguageMismatch]);
  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const getTranslationPreview = useCallback(async (text: string) => {
    if (!translateEnabled || !contactLanguage || !text.trim()) {
      setPreview(null);
      return null;
    }
    setLoading(true);
    try {
      const result = await languageAPI.translate({
        text,
        source_language: userLanguage,
        target_language: contactLanguage,
      });
      setPreview(result.translated_text);
      return result.translated_text;
    } catch {
      setPreview(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [translateEnabled, userLanguage, contactLanguage]);

  const clearPreview = useCallback(() => setPreview(null), []);

  return {
    hasLanguageMismatch,
    translateEnabled,
    setTranslateEnabled,
    preview,
    loading,
    getTranslationPreview,
    clearPreview,
    targetLanguage: contactLanguage,
  };
}
